define((require) => {
    "use strict";

    const Browser                       = require("shared/services/Browser");
    const EditBehavior                  = require("ui/behaviors/EditBehavior");
    const ImageDetailsTrackingBehavior  = require("ui/imageLibrary/ImageDetailsTrackingBehavior");
    const ImageTagCompositeView         = require("ui/assets/image/tags/ImageTagCompositeView");
    const InventoryTagCompositeView     = require("ui/assets/image/inventory/InventoryTagCompositeView");
    const ImageLibrarySourceEnum        = require("shared/data/images/ImageLibrarySourceEnum");
    const ImageTooltipView              = require("ui/assets/image/ImageTooltipView");
    const ImprovedView                  = require("editor/zbone/ImprovedView");
    const template                      = require("ui/assets/image/ImageThumbnailView.tpl");
    const Tooltip                       = require("ui/common/Tooltip");

    // The same dimension we set to the CSS.
    const THUMB_WIDTH = 215;
    const THUMB_HEIGHT = 140;
    const DEFAULT_RATIO = THUMB_WIDTH / THUMB_HEIGHT;

    /**
     * ImprovedView for an image in the image library.
     *
     * @class ImageThumbnailView
     * @constructor
     *
     * @cfg {Boolean} [enableDeletion] Whether to allow deletion of an image via the view, disabled by default.
     * @cfg {Boolean} [showImageInfo] Whether to display info about the image in the view.
     * @cfg {Boolean} [draggable] Whether the image view should be draggable.
     * @cfg {Boolean} [showTooltips] Whether to show a tooltip with image info when the view is
     *                               hovered.
     */
    return ImprovedView.extend({

        _highlighted: false,

        className: "image-wrapper",

        options: {
            enableDeletion: false
        },

        template,

        templateHelpers() {
            return {
                isEditable: this.options.isEditable,
                enableDeletion: this.options.enableDeletion,
                uploading: this.model.has("uploading"),
                timesUsed: this.model.get("timesUsed"),
                absoluteThumbUrl: this.model.get("absoluteThumbUrl"),
                displaySize: this.model.get("displaySize"),
                extension: this.model.get("extension"),
                imageTaggingEnabled: this.options.imageTaggingEnabled,
                inventoryEnabled: this.options.inventoryEnabled
            };
        },

        regions: {
            imageTagsRegion: ".imageTagsRegion",
            inventoryTagsRegion: ".inventoryTagsRegion"
        },

        bindings: {
            ".js-name": {
                // We need to use updateMethod "html" and escape true, because we are using a tag
                // contenteditable.
                observe: "name",
                updateMethod: "html",
                escape: true
            },
            ".js-alt-text": {
                observe: "altText",
                updateMethod: "html",
                escape: true
            }
        },

        behaviors: {
            nameEditBehavior: {
                behaviorClass: EditBehavior,
                container: ".js-name-editable-container",
                editFields: [{
                    input:       ".js-name",
                    icon:        ".js-name-icon",
                    hideOnFocus: [".times-used"],
                    model:       "name",
                    save() {
                        this.model.saveLater();
                    }
                }]
            },
            altTextEditBehavior: {
                behaviorClass: EditBehavior,
                container: ".js-alt-text-editable-container",
                editFields: [{
                    input: ".js-alt-text",
                    icon:  ".js-alt-text-icon",
                    model: "altText",
                    save() {
                        this.model.saveLater();
                    }
                }]
            },
            imageEditingTracking: {
                behaviorClass: ImageDetailsTrackingBehavior,
                tracking: {
                    name: "EditImageNameInputField",
                    altText: "EditImageAltTextInputField"
                }
            }
        },

        ui: {
            deleteImage: ".delete",
            noDeleteImage: ".no-delete",
            imageInfo: ".image-info",
            progressBar: ".progress-bar",
            imgPencil: ".img-pencil",
            timesUsed: ".times-used",
            image: ".image",
            imageThumb: ".image-thumb"
        },

        modelEvents: {
            "change:uploading change:timesUsed change:inventoryTags change:imageTags": "_imageCountChangeHandler",
            "change:progress": "_changeProgressHandler"
        },

        events: {
            click: "_clickHandler",
            dragstart: "_dragStartHandler",
            dragend: "_dragEndHandler",
            "click @ui.imageThumb": "_imageThumbClickHandler",
            "click @ui.deleteImage": "_deleteImageClickHandler"
        },

        initialize() {
            this._highlighted = !!this.options.highlighted;
        },

        /**
         * Marks the image as highlighted, i.e. applying the <tt>highlighted</tt> CSS class.
         *
         * @param  {Boolean} value Whether the image should be highlighted or not.
         */
        highlight(value) {
            this._highlighted = value;

            this._updateHighlightedState();
        },

        _clickHandler() {
            this.trigger("selected", this.model, this);
        },

        _imageThumbClickHandler() {
            this.trigger("image:clicked", this.model, this);
        },

        _deleteImageClickHandler() {
            Tooltip.hide(this.ui.deleteImage);

            if (this.options.isEditable && !this.model.get("timesUsed") && confirm("Are you sure you want to delete this image?")) {
                this.model.destroy();
            }
        },

        _imageCountChangeHandler() {
            this.render();
        },

        _updateHighlightedState() {
            if (this.el) {
                this.$el.toggleClass("highlighted", this._highlighted);
            }
        },

        _changeProgressHandler(imageModel, progress) {
            this._updateUploadingBar(progress);
        },

        _dragStartHandler(event) {
            // Prevent default dragging behavior and bail out if "dragging" is not enabled.
            if (!this.options.draggable) {
                return event.preventDefault();
            }
            // Firefox has a bug that causes it to offset the dragged image when displayed in a lightbox. Workaround is
            // to explicitly set the drag image to the dragged element.
            if (Browser.type.firefox) {
                event.originalEvent.dataTransfer.setDragImage(this.el, this.$el.width() / 2, this.$el.height() / 2);
            }

            const data = {
                type: "image",
                options: {
                    imageID: this.model.id
                }
            };

            if (this.model.get("sourceType")) {
                data.options.sourceType = this.model.get("sourceType");
            }

            event.originalEvent.dataTransfer.setData("Text", JSON.stringify(data));

            this.$el.parents("body").addClass("internal-drag");
            // Cancel showing the bootstrap popover.
            this._closeTooltip();
        },

        _dragEndHandler() {
            this.$el.parents("body").removeClass("internal-drag");
        },

        onRender() {
            const {
                draggable,
                imageTaggingEnabled,
                showImageInfo,
                showTooltips,
                showTimesUsedTooltip,
                inventoryEnabled,
                isEditable
            } = this.options;

            const {
                timesUsed,
                noDeleteImage,
                deleteImage
            } = this.ui;

            this.$el.attr("draggable", !!draggable);

            if (inventoryEnabled) {
                const tagsCollection = this.model.get("inventoryTags");
                const limitedTagCollection = tagsCollection.newCopySortedByConfidence(5);

                // Make sure the copied version of the collection gets updated when removing or adding new models
                this.listenTo(tagsCollection, "add remove sort", () => {
                    limitedTagCollection.reset(tagsCollection.newCopySortedByConfidence(5).models);
                });

                this.inventoryTagsRegion.show(new InventoryTagCompositeView({
                    tagCollection: this._tagsCollection,
                    collection: limitedTagCollection
                }));
            }

            if (imageTaggingEnabled) {
                const tagsCollection = this.model.get("imageTags");
                const limitedTagCollection = tagsCollection.newCopySortedByConfidence(5);

                // Make sure the copied version of the collection gets updated when removing or adding new models
                this.listenTo(tagsCollection, "add remove sort", () => {
                    limitedTagCollection.reset(tagsCollection.newCopySortedByConfidence(5).models);
                });

                this.imageTagsRegion.show(new ImageTagCompositeView({
                    tagCollection: this._tagsCollection,
                    collection: limitedTagCollection
                }));
            }

            if (!showImageInfo) {
                this.ui.imageInfo.remove();
            }

            const isScene7 = this.model.isSource(ImageLibrarySourceEnum.SCENE7);

            if (showTooltips) {
                Tooltip.add(this.$el, {
                    content: new ImageTooltipView({
                        model: this.model,
                        showExtension: !isScene7,
                        showTags: imageTaggingEnabled && !isScene7,
                        isScene7
                    }),
                    container: "body",
                    width: 200,
                    html: true,
                    offset: {
                        align: "right",
                        elementWidth: 50
                    },
                    delay: {
                        show: 1000,
                        hide: 0
                    }
                });
            }

            if (showTimesUsedTooltip) {
                Tooltip.add(timesUsed, {
                    placement: "top",
                    alignCenter: true,
                    width: 220
                });
            }

            // Tooltips for delete button.
            Tooltip.add(noDeleteImage, {
                placement: "top",
                alignCenter: true,
                width: 260
            });

            Tooltip.add(deleteImage, {
                placement: "top",
                alignCenter: true,
                width: isEditable ? 150 : 180
            });

            this._updateHighlightedState();
            this.scaleUp();
        },

        /**
         * This operation is for backwards compatibility (before 20 Oct. 2015) so that images will
         * fit the new box size 215 x 140. To avoid blurry images by scaling up small icons, we
         * only apply it to images wider than 190 or taller than 140.
         */
        scaleUp() {
            if (this.model.get("width") >= 190 || this.model.get("height") >= 140) {
                const ratio = this.model.get("width") / this.model.get("height");
                const cssClass = ratio > DEFAULT_RATIO ? "horizontal-scale" : "vertical-scale";

                this.ui.image.addClass(cssClass);
            }
        },

        /**
         * Close the boostrap popover if the showToolTips option is true
         */
        _closeTooltip() {
            if (this.options.showTooltips) {
                this.$el.trigger("mouseout");
            }
        },

        onClose() {
            this._closeTooltip();
        },

        _showProgress() {
            return this.model.get("uploading");
        },

        _updateUploadingBar(progress) {
            this.ui.progressBar.width(progress * 100 + "%");
        }

    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/assets/image/ImageThumbnailView.js